import {
	Checkbox,
	DateTimePicker,
	Field,
	FieldInformation,
	Heading,
	Text,
} from '@components'
import type { CreateSetup } from '@here/api'
import { type FormStore, getValue, setValue } from '@modular-forms/solid'
import { For, Match, Show, Switch, createEffect, createSignal } from 'solid-js'
import { css } from 'styled-system/css'
import { Grid } from 'styled-system/jsx'
import * as v from 'valibot'
import type { FormFieldType } from '.'
import { OptionalField } from '../../../../components/custom/OptionalDetails'
import { FieldGroupContainer } from './FieldGroupContainer'
import { TechnicalSchema } from './createSetupSchema'
import { fieldInformation } from './fieldInformation'

export default function Technical(props: {
	FormField: FormFieldType
	form: FormStore<CreateSetup, undefined>
	hideNonTechnicalFields?: boolean
}) {
	const [isValid, setIsValid] = createSignal(false)
	const [HVFErrors, setHVFErrors] = createSignal<string[]>([])
	const [formFields, setFormFields] = createSignal<Array<keyof CreateSetup>>([
		'high_1_price',
		'high_1_time',
		'high_2_price',
		'high_2_time',
		'low_2_price',
		'low_2_time',
		'high_3_price',
		'high_3_time',
		'low_3_price',
		'low_3_time',
	])

	createEffect(() => {
		const position = getValue(props.form, 'position') as string

		const newOrder: Array<keyof CreateSetup> =
			position === 'short'
				? [
						'low_1_price',
						'low_1_time',
						'high_1_price',
						'high_1_time',
						'low_2_price',
						'low_2_time',
						'high_2_price',
						'high_2_time',
						'low_3_price',
						'low_3_time',
						'high_3_price',
						'high_3_time',
					]
				: [
						'high_1_price',
						'high_1_time',
						'low_1_price',
						'low_1_time',
						'high_2_price',
						'high_2_time',
						'low_2_price',
						'low_2_time',
						'high_3_price',
						'high_3_time',
						'low_3_price',
						'low_3_time',
					]

		setFormFields(newOrder)
	})

	createEffect(() => {
		const formData = formFields().reduce(
			(prev, curr) => {
				prev[curr] = getValue(props.form, curr) as number
				return prev
			},
			{} as Record<keyof CreateSetup, string | number | null | undefined>,
		)

		const validateResult = v.safeParse(TechnicalSchema, formData)

		const hvfErrors =
			validateResult.issues
				?.filter(({ message }) => message.includes('HVF_ERROR'))
				.map(({ message }) => message) || []

		setHVFErrors(hvfErrors)

		setIsValid(validateResult.success)
	})

	const getTitle = (key: keyof CreateSetup) => {
		const isShort = getValue(props.form, 'position') === 'short'

		const map: Partial<Record<keyof CreateSetup, string>> = {
			high_1_price: isShort ? 'RH1' : 'H1',
			high_2_price: 'RH2',
			high_3_price: 'RH3',
			low_1_price: isShort ? 'L1' : 'RL1',
			low_2_price: 'RL2',
			low_3_price: 'RL3',
		}

		return map[key]
	}

	return (
		<FieldGroupContainer
			heading="Technical"
			valid={isValid()}
		>
			<Grid>
				<Grid gridTemplateColumns="1fr 2fr">
					<FieldInformation information={fieldInformation.high_1_price}>
						<Heading size="xs">Price</Heading>
					</FieldInformation>
					<FieldInformation information={fieldInformation.high_1_time}>
						<Heading size="xs">Time</Heading>
					</FieldInformation>

					<For each={formFields()}>
						{(formField) => (
							<props.FormField
								name={formField}
								type={formField.includes('time') ? 'string' : 'number'}
							>
								{(field, formFieldProps) => (
									<Switch>
										<Match when={formField.includes('time')}>
											<Field.Root invalid={!!field.error}>
												<OptionalField children={' '} />
												<DateTimePicker
													dateFormat="DD MMM, YYYY HH:mm"
													enableDateInputField={true}
													enableCalendarViewType={true}
													ednableTimeEditing={true}
													enableTimeView={true}
													onChange={(value) =>
														setValue(props.form, formField, value || undefined)
													}
													currentDate={field.value as string}
												/>
											</Field.Root>
										</Match>
										<Match when={formField.includes('price')}>
											<Field.Root invalid={!!field.error}>
												<Field.Label>{getTitle(formField)}</Field.Label>
												<Field.Input
													{...formFieldProps}
													value={field.value as any}
													type={'number'}
													onChange={(event) => {
														let number: number | undefined = Number(event.target.value)
														number = Number.isNaN(number) ? undefined : number
														setValue(props.form, formField, number)
													}}
												/>
												<Field.ErrorText>{<div>{field.error}</div>}</Field.ErrorText>
											</Field.Root>
										</Match>
									</Switch>
								)}
							</props.FormField>
						)}
					</For>
				</Grid>

				<Grid>
					<For each={HVFErrors()}>
						{(error) => (
							<Text
								children={error.replace('HVF_ERROR: ', '')}
								class={css({
									color: 'red.10',
								})}
							/>
						)}
					</For>
				</Grid>

				<Show when={!props.hideNonTechnicalFields}>
					<Heading size="xs">Details</Heading>

					<Grid
						gridTemplateColumns={{
							base: '1fr 1fr',
							mdDown: '1fr',
						}}
					>
						<props.FormField
							name="first_in_new_trend"
							type="boolean"
						>
							{(field, formFieldProps) => (
								<Field.Root invalid={!!field.error}>
									<Checkbox
										checked={getValue(props.form, 'first_in_new_trend') as any}
										onCheckedChange={(v) =>
											setValue(props.form, 'first_in_new_trend', v.checked as boolean)
										}
									>
										<FieldInformation information={fieldInformation.first_in_new_trend}>
											First in a new trend
										</FieldInformation>
									</Checkbox>
								</Field.Root>
							)}
						</props.FormField>

						<props.FormField
							name="has_primer"
							type="boolean"
						>
							{(field, formFieldProps) => (
								<Field.Root invalid={!!field.error}>
									<Checkbox
										checked={getValue(props.form, 'has_primer') as any}
										onCheckedChange={(v) =>
											setValue(props.form, 'has_primer', v.checked as boolean)
										}
									>
										<FieldInformation information={fieldInformation.has_primer}>
											Has Primer
										</FieldInformation>
									</Checkbox>
								</Field.Root>
							)}
						</props.FormField>

						<props.FormField name="trigger_date">
							{(field, formFieldProps) => {
								return (
									<Field.Root invalid={!!field.error}>
										<FieldInformation information={fieldInformation.trigger_date}>
											<OptionalField children="Triger Date" />
										</FieldInformation>
										<DateTimePicker
											dateFormat="DD MMM, YYYY HH:mm"
											onChange={(value) => {
												if (value) {
													setValue(props.form, 'trigger_date', value)
												}
											}}
											currentDate={field.value as string}
											enableDateInputField={true}
											enableCalendarViewType={true}
											ednableTimeEditing={true}
											enableTimeView={true}
										/>
									</Field.Root>
								)
							}}
						</props.FormField>

						<props.FormField name="time_stop">
							{(field, formFieldProps) => (
								<Field.Root invalid={!!field.error}>
									<FieldInformation information={fieldInformation.time_stop}>
										<OptionalField>Time Stop</OptionalField>
									</FieldInformation>
									<DateTimePicker
										dateFormat="DD MMM, YYYY HH:mm"
										enableDateInputField={true}
										enableCalendarViewType={true}
										ednableTimeEditing={true}
										enableTimeView={true}
										onChange={(value) => {
											if (value) {
												setValue(props.form, 'time_stop', value)
											}
										}}
										currentDate={field.value as string}
									/>
									<Field.ErrorText>
										{field.error && <div>{field.error}</div>}
									</Field.ErrorText>
								</Field.Root>
							)}
						</props.FormField>
					</Grid>
				</Show>
			</Grid>
		</FieldGroupContainer>
	)
}
